import { handleGetRequest } from "./GetTemplate";
import { handlePostRequest } from "./PostTemplate";

// Admin and clerk can both record a payment here across a client's
// outstanding invoices. Wire never touches a register. Cash requires picking
// which cash register the money went into (see the cashRegisterId field
// PaymentAllocationPanel shows once "Cash" is selected) so it lands in the
// Cash Ledger like any other cash movement. "Card" is never a manual entry -
// it always means the real payment gateway (see the in-store-pickup plan ->
// charge -> commit flow), so it's deliberately absent here too.
export const PAYMENT_METHODS_BY_ROLE = {
    admin: [
        { label: "Wire Transfer", value: "wire" },
        { label: "Cash", value: "cash" },
    ],
    clerk: [
        { label: "Wire Transfer", value: "wire" },
        { label: "Cash", value: "cash" },
    ],
};

export const searchClients = (query) => handleGetRequest(`/client-payment/search-clients?query=${encodeURIComponent(query || "")}`);

export const fetchOutstandingInvoices = (clientId, flightNumber) => {
    const params = new URLSearchParams({ clientId });
    if (flightNumber) params.set("flightNumber", flightNumber);
    return handleGetRequest(`/client-payment/outstanding?${params.toString()}`);
};

export const processClientPayment = (dispatch, payload) => dispatch(handlePostRequest(payload, "/client-payment/process", true, true));

export const fetchAdminPaymentHistory = (clientId, paymentMethod, from, to, flightNumber) => {
    const params = new URLSearchParams();
    if (clientId) params.set("clientId", clientId);
    if (paymentMethod) params.set("paymentMethod", paymentMethod);
    if (flightNumber) params.set("flightNumber", flightNumber);
    if (from && to) {
        params.set("from", from);
        params.set("to", to);
    }
    const qs = params.toString();
    return handleGetRequest(`/client-payment/admin/history${qs ? `?${qs}` : ""}`);
};

// Payments clients made themselves (card/PayPal/Amazon Pay from their own
// account) or that an admin recorded via the older single/bulk mark-paid
// actions - never went through the ledger above, so this reconstructs them
// from the invoices directly.
export const fetchAdminOtherPayments = (clientId, from, to, flightNumber) => {
    const params = new URLSearchParams();
    if (clientId) params.set("clientId", clientId);
    if (flightNumber) params.set("flightNumber", flightNumber);
    if (from && to) {
        params.set("from", from);
        params.set("to", to);
    }
    const qs = params.toString();
    return handleGetRequest(`/client-payment/admin/other-payments${qs ? `?${qs}` : ""}`);
};

// Rolled-up stats across every payment source (ledger, legacy/self-service
// invoice payments, cash register, wire) for whichever client and/or flight
// is currently filtered - only meaningful once at least one of those is set.
export const fetchAdminStats = (clientId, flightNumber) => {
    const params = new URLSearchParams();
    if (clientId) params.set("clientId", clientId);
    if (flightNumber) params.set("flightNumber", flightNumber);
    const qs = params.toString();
    return handleGetRequest(`/client-payment/admin/stats${qs ? `?${qs}` : ""}`);
};

// Page-level "how much collected, by method" overview - independent of
// adminStats above, which only runs once a client/flight is picked.
export const fetchAdminMethodStats = (clientId, flightNumber, from, to) => {
    const params = new URLSearchParams();
    if (clientId) params.set("clientId", clientId);
    if (flightNumber) params.set("flightNumber", flightNumber);
    if (from && to) {
        params.set("from", from);
        params.set("to", to);
    }
    const qs = params.toString();
    return handleGetRequest(`/client-payment/admin/method-stats${qs ? `?${qs}` : ""}`);
};

// A row is "fully paid" once amountApplied covers what was owed, "partially
// paid" (labelled with the exact amount applied) if it covers some but not
// all of it, and otherwise untouched by this payment.
export const paymentRowStatus = (row) => {
    const balanceBefore = row.balanceBefore ?? row.invoiceAmount ?? 0;
    const amountApplied = row.amountApplied || 0;

    if (row.fullyPaid || (amountApplied > 0 && balanceBefore - amountApplied <= 0.01)) {
        return { label: "Paid", badgeClass: "badgePaid" };
    }
    if (amountApplied > 0) {
        return { label: `Partially Paid ${formatMoney(amountApplied)}`, badgeClass: "badgePartial" };
    }
    return { label: "Untouched", badgeClass: "badgeUntouched" };
};

export const formatMoney = (value, currency = "usd") => {
    if (value === null || value === undefined || isNaN(value)) return "-";
    return `${currency === "jmd" ? "J$" : "$"}${Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// A fresh id per "payment session" (i.e. per mount of the confirm step), so a
// double-click or network retry on submit reuses the same key and the
// backend's idempotency check can catch it - regenerated only when the
// staff member starts allocating a genuinely new payment. Prefix lets other
// idempotency-keyed actions (e.g. grantAccountCredit below) reuse this
// without their keys colliding with a payment's.
export const generateIdempotencyKey = (prefix = "pay") => `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;

// Reclassifies money already held - reverses a specific invoice's paid
// amount to account credit (optionally reopening it as a fresh invoice,
// same client or a different one), or grants goodwill credit unrelated to
// any invoice. See controllers/clientPayment.js's grantAccountCredit.
export const grantAccountCredit = (dispatch, payload) => dispatch(handlePostRequest(payload, "/client-payment/admin/grant-credit", true, true));

// Aggregate "how much credit is currently owed back to clients" figure -
// not scoped to any one client, unlike accountCreditBalance elsewhere on
// this page.
export const fetchAdminCreditStats = () => handleGetRequest("/client-payment/admin/credit-stats");

// Every CreditAdjustment for one client, newest first - the actual audit
// trail (grants, reversals, manual corrections) behind their current
// accountCreditBalance.
export const fetchCreditHistory = (clientId) => handleGetRequest(`/client-payment/admin/credit-history/${clientId}`);

// Every client currently holding a positive credit balance, highest first -
// the directory used to browse to one and drill into fetchCreditHistory.
export const fetchCreditClients = () => handleGetRequest("/client-payment/admin/credit-clients");
